'use client';
import { debounce } from '@smartproxy-web/shared/utils';
import { useSearchParams } from 'next/navigation';
import { useEffect, useReducer, useRef, useState } from 'react';

import { useClickOutside } from '@/hooks/useClickOutside';

import { EntryWithID } from '@/components/cms/types/entry';

import IconClose from '~/svg/IconCross.svg';
import IconSearch from '~/svg/IconSearch.svg';
import {
  generateInitialState,
  reducer,
} from '@/decodo/blocks/HeroWithSearch/state';
import { searchPages } from '@/decodo/blocks/HeroWithSearch/service';
import Button from '@/decodo/components/atoms/Button';
import { SearchBarInputField } from '@/decodo/blocks/HeroWithSearch/SearchInputField';
import SearchResultList from '@/decodo/blocks/HeroWithSearch/SearchResultsList';
import { cn } from '@/lib/utils';

type SearchProps = {
  initialFaq?: {
    data: EntryWithID[];
  };
  slug?: string;
  searchWithin?: 'faq';
};

export function SearchBar({ initialFaq, slug, searchWithin }: SearchProps) {
  const searchParams = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [state, dispatch] = useReducer(
    reducer,
    { results: initialFaq, title: searchParams.get('title') },
    generateInitialState
  );

  const searchBarRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setQuery(value);

    setIsDropdownOpen(value.length > 0);

    const debouncedSetIsDropdownOpen = debounce((open: boolean) => {
      setIsDropdownOpen(open);
    }, 50);

    if (value.length < 2) {
      debouncedSetIsDropdownOpen(false);
    }
  };

  useClickOutside({
    ref: searchBarRef as React.MutableRefObject<HTMLElement>,
    callback: () => {
      setIsDropdownOpen(false);
    },
  });

  const handleClearInput = () => {
    setQuery('');
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    async function filter() {
      dispatch({ type: 'loading_pages' });

      const result = await searchPages({
        slug,
        query,
        searchWithin: searchWithin ?? 'faq',
      });

      if (!result) {
        dispatch({ type: 'no_pages', payload: { query } });
        return;
      } else {
        return dispatch({
          type: 'searched_pages',
          payload: {
            results: result,
          },
        });
      }
    }
    filter();
  }, [query, searchWithin, slug]);

  const rightCloseIcon = (
    <Button onClick={handleClearInput} className='text-base-black z-50'>
      <IconClose className='h-6 w-6' />
    </Button>
  );

  return (
    <div ref={searchBarRef} className='group relative'>
      <div className='w-full max-w-[600px]'>
        <SearchBarInputField
          value={query}
          onChange={handleInputChange}
          classNames={{
            inputField: cn(
              isDropdownOpen &&
                '!border-b-0 rounded-b-none !border-brand-500 w-full group-focus-within:border-2'
            ),
          }}
          rightIcon={
            isDropdownOpen ? (
              rightCloseIcon
            ) : (
              <IconSearch className='text-base-black h-6' />
            )
          }
        />
      </div>
      <div className='group-focus-within'>
        {isDropdownOpen ? (
          state.results.length > 0 ? (
            <div className='relative w-full max-w-[600px]'>
              <ul className='input-dropdown-animation border-brand-500 scrollbar-tiny dark:scrollbar-dark bg-base-white absolute max-h-[260px] w-full overflow-y-auto rounded-b-md border-x-2 border-b-2 pr-[22px]'>
                <SearchResultList results={state.results} />
              </ul>
            </div>
          ) : (
            <p className='input-dropdown-animation border-brand-500 bg-base-white absolute w-full max-w-[600px] items-center rounded-b-md border-x-2 border-b-2 px-6 py-3 font-serif text-gray-500'>
              No results
            </p>
          )
        ) : null}
      </div>
    </div>
  );
}
